import { Link } from 'react-router-dom'
import { useEventQuoteRequests } from '@/hooks/useQuoteRequests'
import { requestErrorMessage } from '@/helpers/apiClient'
import { ErrorState, LoadingState } from '@/components/ui/states'
import { buttonVariants } from '@/components/ui/button'
import { VendorProfileLink } from '@/components/VendorProfileLink'
import { AskForQuoteButton } from '@/components/quotes/QuoteRequestDialog'
import type { QuoteRequestDto } from '@lib/dto/quote-request.dto'
import type { QuoteRequesterRole } from '@lib/enums/quote-requester-role.enum'

const SENT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })

/** Who asked, as the event's planner and the venue's staff read it. */
export function requesterLabel(role: QuoteRequesterRole, company: string | null): string {
  switch (role) {
    case 'couple':
      return 'Couple'
    case 'planner':
      return company === null ? 'Planner' : `Planner, ${company}`
    case 'venue':
      return 'Venue staff'
  }
}

interface Props {
  eventId: string
  /** The couple sees only what it asked, so only which of them asked. */
  asCouple: boolean
  /**
   * How this reader asks, while they may: the couple and the planner pick from
   * the venue's directory; the venue's staff choose a vendor in the dialog.
   */
  ask: 'directory' | 'dialog' | null
}

function Request({ request, asCouple }: { request: QuoteRequestDto; asCouple: boolean }) {
  return (
    <li className="space-y-1 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-medium">
          <VendorProfileLink vendorId={request.vendor.vendorId} name={request.vendor.name} />
        </span>
        <span className="text-xs text-ink-muted">{SENT.format(new Date(request.sentAt))}</span>
      </div>
      <p className="m-0 text-ink-muted">{request.packages.length === 0 ? 'No package chosen' : request.packages.join(', ')}</p>
      {/* The couple's own requests: which of them asked, for two partners on one event. */}
      <p className="m-0 text-xs text-ink-muted">
        {asCouple ? `Asked by ${request.requester.name}` : `${request.requester.name} · ${requesterLabel(request.requester.role, request.requester.company)}`}
      </p>
      <details>
        <summary className="cursor-pointer text-xs text-ink-muted">Message</summary>
        <p className="m-0 mt-1 whitespace-pre-line">{request.message}</p>
      </details>
    </li>
  )
}

/**
 * Who has asked whom for a quote for the event (docs/vendor-growth.spec.md,
 * "Quote requests"): the couple's own requests for a couple; all of them for
 * the planner and the venue's staff.
 */
export function EventQuoteRequests({ eventId, asCouple, ask }: Props) {
  const requests = useEventQuoteRequests(eventId)

  function list() {
    if (requests.isPending) return <LoadingState label="Loading quote requests…" />
    if (requests.isError) return <ErrorState message={requestErrorMessage(requests.error)} onRetry={() => void requests.refetch()} />
    if (requests.data.requests.length === 0) {
      return (
        <p className="m-0 text-sm text-ink-muted">
          {asCouple ? 'You haven’t asked any vendors for a quote yet.' : 'No quote requests yet.'}
        </p>
      )
    }
    return (
      <ul className="m-0 list-none divide-y divide-line overflow-hidden rounded-md border border-line p-0">
        {requests.data.requests.map((request) => (
          <Request key={request.id} request={request} asCouple={asCouple} />
        ))}
      </ul>
    )
  }

  return (
    <div className="space-y-3">
      {list()}
      {ask === 'directory' ? (
        <Link to={`/events/${eventId}/vendors`} className={buttonVariants({ variant: 'outline', size: 'sm' })}>
          Ask a vendor for a quote
        </Link>
      ) : null}
      {ask === 'dialog' ? <AskForQuoteButton eventId={eventId} label="Ask a vendor for a quote" /> : null}
    </div>
  )
}
